/**
 * Which deployment the browser is actually looking at, keyed on hostname.
 *
 * Hostname — NOT `REACT_APP_ENVIRONMENT`. The staging build (`.env_staging`)
 * reports `REACT_APP_ENVIRONMENT = 'development'`, identical to local/dev, so
 * that variable cannot tell staging from dev and cannot be trusted to say
 * "this is production" either. The hostname is the reliable client-side
 * signal. This was established by `src/App/envFavicon.ts` (which now reads its
 * lists from here) and the hosts below are its verified list, unchanged.
 *
 * Hosts, verified 2026-08-01 by probing them plus the repo's own config:
 *   prod    → app.wastehero.io       (catalog-info.yaml, "WasteHero 1.0
 *                                     Production"; platform.wastehero.io —
 *                                     still PUBLIC_URL in .env.production —
 *                                     301s here, so the live tab's hostname is
 *                                     app.*)
 *   prod    → platform.wastehero.io  (kept: it only redirects today, and
 *                                     someone may still hit it directly)
 *   staging → app-staging.wastehero.io (re-checked, still serving 200)
 * Everything else (localhost, app-development.wastehero.io, *.vercel.app and
 * *.preview-frontend.wastehero.io previews) is neither.
 *
 * Domains move: add the new host to the list below, don't rework the lookup.
 * A host that is production and is NOT listed here reads as non-production,
 * which is the fail-OPEN direction — so anything that gates on
 * `isProductionHost()` for safety must pair it with a second condition that
 * fails closed on its own. That rule is guidance for a future caller, not a
 * description of live behaviour: the exported arrays are consumed by
 * `src/App/envFavicon.ts` to pick a favicon, while the `isProductionHost` /
 * `isStagingHost` predicates currently have no callers — the design toggle's
 * use of them was removed when its access gate was dropped.
 */
export const PRODUCTION_HOSTNAMES = [
  'app.wastehero.io',
  'platform.wastehero.io',
]
export const STAGING_HOSTNAMES = ['app-staging.wastehero.io']

const currentHostname = (): string =>
  typeof window !== 'undefined' ? window.location.hostname : ''

export const isProductionHost = (
  hostname: string = currentHostname()
): boolean => PRODUCTION_HOSTNAMES.includes(hostname)

export const isStagingHost = (hostname: string = currentHostname()): boolean =>
  STAGING_HOSTNAMES.includes(hostname)
